import * as React from 'react'

import { ProductV2, getLocale, GameId } from '@common'
import Head from 'next/head'
import { useTranslations } from 'next-intl'

import { api } from '@/api'
import { PageMetaTags } from '@/components/MetaTags'
import { Header } from '@/features/storefronts/_storeBase/header/Header'
import { GameStoreProduct } from '@/features/storefronts/_storeBase/productPage/GameStoreProduct'
import { StorefrontWrapper } from '@/features/storefronts/_storeBase/structure/StorefrontWrapper'
import { loadProductCopy } from '@/localization/MessageContext'
import { useTranslatable } from '@/localization/useTranslatable'
import { gameIdToGameNameKey } from '@/localization/utils'
import { adminService } from '@/services/AdminService'
import { cartClient } from '@/services/cartClient'
import { getVisibleProducts } from '@/services/scid/SCID'
import { setPublicPageCacheControl, wrapGetServerSideProps } from '@/ssr/wrapper'
import { getAccountId } from '@/utils/getAccountId'
import { findProductBySlugOrId } from '@/utils/products'

import { config as envConfig } from '../../../../config'

const ProductPage: React.FC<{
  gameId: GameId
  product: ProductV2
}> = ({ gameId, product }) => {
  const t = useTranslations()
  const translatable = useTranslatable()

  const productTitle = translatable(product.title)
  const title = `${t('route_product', {
    product: productTitle ?? '',
    game: t(gameIdToGameNameKey(gameId)),
  })}`

  return (
    <StorefrontWrapper gameId={gameId}>
      <Header game={gameId} />
      <PageMetaTags
        title={translatable(product.title)}
        description={t('gen_prod_meta_description', { product: translatable(product.title) })}
      />
      <Head>
        <title>{title}</title>
      </Head>
      <GameStoreProduct product={product} isProductPage />
    </StorefrontWrapper>
  )
}

export default ProductPage

const noSuchProductError = {
  notFound: true,
} as const

export const getServerSideProps = wrapGetServerSideProps(
  async (store, context, isRecognized, enabledStoreFronts, market) => {
    const slug = context.params?.slug
    const gameSlug = context.params?.gameSlug as GameId

    if (!slug || !gameSlug) {
      return noSuchProductError
    }

    const productId = Array.isArray(slug) && slug[slug.length - 1]
    const viewerCountry = store.getState().config.viewerCountry
    const accountId = getAccountId(context)
    const isTestAccount = Boolean(accountId && (await adminService.isTestAccount(accountId)))

    const isDisabledCountry =
      (viewerCountry && envConfig.SQUAD_DISABLED_COUNTRIES.includes(viewerCountry)) || isTestAccount
    if (gameSlug === 'squadbusters' && isDisabledCountry) {
      return noSuchProductError
    }

    if (!productId) {
      return noSuchProductError
    }

    if (enabledStoreFronts.length === 0) {
      return noSuchProductError
    }

    setPublicPageCacheControl(context, 30)
    const locale = getLocale(context)

    const getProductsP = getVisibleProducts(
      accountId,
      isTestAccount,
      gameSlug,
      {
        locale,
        currencyCountry: market.currencyCountry,
      },
      {},
    )
    const getCartP = cartClient.getCart(accountId, gameSlug)

    const [products, cart] = await Promise.all([getProductsP, getCartP])

    const product = products.find(findProductBySlugOrId(productId)) ?? null

    if (!product) {
      return noSuchProductError
    }

    await Promise.all(store.dispatch(api.util.getRunningQueriesThunk()))

    const productCopy = await loadProductCopy(product, context.locale || 'en')
    const pageMessages = { [product.slug]: productCopy }
    const { api: apiState, config } = store.getState()

    return {
      props: {
        gameId: gameSlug,
        products,
        cart,
        product,
        initialState: { api: apiState, config },
      },
      messages: pageMessages,
    }
  },
  'productSSR',
)
